import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Modal } from './Modal';
import { Button } from './Button';
import { Input, Textarea } from './Input';
import { FormField } from './FormField';

/**
 * Hộp thoại nhập liệu dùng chung — thay window.prompt.
 *
 *   const prompt = usePrompt();
 *   const value = await prompt({ title, message, label, placeholder, defaultValue, required, multiline, inputType });
 *   (inputType: kiểu ô một dòng — 'text' mặc định, hoặc 'date', 'email', 'number'...)
 *   if (value == null) return; // người dùng bấm Hủy
 *
 * Bọc <PromptProvider> quanh app (đã lồng trong ConfirmProvider ở App.js).
 */
const PromptContext = createContext(null);

export function PromptProvider({ children }) {
  const [state, setState] = useState({ open: false, opts: {} });
  const [value, setValue] = useState('');
  const resolverRef = useRef(null);

  const prompt = useCallback((opts = {}) => {
    setValue(opts.defaultValue || '');
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setState({ open: true, opts });
    });
  }, []);

  const settle = useCallback((val) => {
    setState((s) => ({ ...s, open: false }));
    if (resolverRef.current) {
      resolverRef.current(val);
      resolverRef.current = null;
    }
  }, []);

  const opts = state.opts || {};
  const trimmed = (value || '').trim();
  const canSubmit = opts.required ? trimmed.length > 0 : true;

  const submit = () => {
    if (!canSubmit) return;
    settle(value);
  };

  return (
    <PromptContext.Provider value={prompt}>
      {children}
      <Modal
        open={state.open}
        onClose={() => settle(null)}
        title={opts.title || 'Nhập thông tin'}
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => settle(null)}>
              {opts.cancelText || 'Hủy'}
            </Button>
            <Button variant={opts.variant || 'primary'} onClick={submit} disabled={!canSubmit}>
              {opts.confirmText || 'Xác nhận'}
            </Button>
          </>
        }
      >
        <FormField label={opts.label} required={opts.required} helper={opts.helper}>
          {opts.message ? <p className="text-sm text-body mb-3 whitespace-pre-line">{opts.message}</p> : null}
          <PromptField
            multiline={opts.multiline}
            value={value}
            onChange={setValue}
            placeholder={opts.placeholder}
            onEnter={submit}
            open={state.open}
            inputType={opts.inputType}
          />
        </FormField>
      </Modal>
    </PromptContext.Provider>
  );
}

function PromptField({ multiline, value, onChange, placeholder, onEnter, open, inputType }) {
  const ref = useRef(null);
  useEffect(() => {
    if (open) {
      const t = setTimeout(() => ref.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [open]);
  if (multiline) {
    return (
      <Textarea ref={ref} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} rows={3} />
    );
  }
  return (
    <Input
      ref={ref}
      type={inputType || 'text'}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          onEnter?.();
        }
      }}
    />
  );
}

export function usePrompt() {
  const ctx = useContext(PromptContext);
  if (!ctx) {
    return async (opts = {}) =>
      typeof window !== 'undefined' && typeof window.prompt === 'function'
        ? window.prompt(opts.message || opts.title || '', opts.defaultValue || '')
        : null;
  }
  return ctx;
}

export default PromptProvider;
